import React, { useEffect, useState } from 'react';
import _ from 'lodash';
import styled from 'styled-components';

import { getUserPreferredFullName, stripHTML } from 'utils/helper';
import { Box, Divider, RichTextArea, Stack, Text, TextInput, Tooltip } from 'shell/components';
import { useSnackbar } from 'contexts/snackbar';

const ZeroSumGameTable = ({
    commentOnly = false,
    isPreviewing = false,
    assignedPercentage = 0,
    parentErrors,
    member = {},
    currentPeerEvaluation,
    submissionData = {},
    handleInputChanges,
}) => {
    const { message } = useSnackbar();

    const [errors, setErrors] = useState({ rate: false, details: false });
    const [hasChange, setHasChange] = useState(false);

    useEffect(() => {
        setErrors((current) => ({ ...current, ...parentErrors }));
    }, [parentErrors]);

    const handleInputs = (key, value) => {
        switch (key) {
            case 'rate': {
                if (value?.length > 0 && isNaN(value)) {
                    setErrors((current) => ({ ...current, rate: true }));
                    return message.error('Please enter numbers only.');
                }
                if (value?.length === 0) return handleInputChanges(member.owner_account_id, 'rate', '');
                const parsedValue = parseInt(value || 0);
                if (!_.isInteger(parsedValue) || parsedValue < 0 || parsedValue > 100) {
                    setErrors((current) => ({ ...current, rate: true }));
                    return message.error('Please check your rate.');
                }
                setHasChange(true);
                setErrors((current) => ({ ...current, rate: false }));
                return handleInputChanges(member.owner_account_id, 'rate', `${parsedValue}`);
            }
            case 'details': {
                if (currentPeerEvaluation?.is_comment_required && hasChange) {
                    if (currentPeerEvaluation?.min_feedback_length > value.length) {
                        setErrors((errors) => ({ ...errors, details: true }));
                    } else {
                        setErrors((errors) => ({ ...errors, details: false }));
                    }
                }
                setHasChange(true);
                return handleInputChanges(member.owner_account_id, 'details', value);
            }
            default:
                return;
        }
    };

    const getLeftPercentage = () => {
        const percentage = Math.round(100 - parseInt(assignedPercentage));
        return percentage;
    };

    const renderCommentOnlySubmittedView = () => {
        return (
            <TableBox key={member?.owner_account_id}>
                <HeaderStack direction={'row'} spacing={1}>
                    <Text variant={'p2'} weight={'medium'}>
                        Explain why this rating was given.
                    </Text>
                </HeaderStack>
                <BodyStack spacing={7}>
                    <Stack spacing={3} sx={{ minHeight: 10 }}>
                        {submissionData.details?.length ? (
                            <Text rich variant={'p2'}>
                                {`${submissionData.details || ''} (${
                                    stripHTML(submissionData.details)?.length || 0
                                } characters)`}
                            </Text>
                        ) : (
                            <Text color={'disabled'} variant={'p2'}>
                                No comment submitted
                            </Text>
                        )}
                    </Stack>
                </BodyStack>
            </TableBox>
        );
    };

    const renderCommentOnlyView = () => {
        return (
            <TableBox key={member?.owner_account_id}>
                <HeaderStack direction={'row'} justifyContent={'space-between'} spacing={1}>
                    <Text variant={'p2'} weight={'medium'}>
                        Explain why this rating was given.
                    </Text>
                    <Tooltip
                        title={
                            <Text color={'inherit'} variant={'p2'}>
                                {currentPeerEvaluation.comment ||
                                    'Tips: Be honest, but professional. Provide detailed explanations of areas that your teammate did well in and areas they can improve. Explain how improving this area can benefit the individual.'}
                            </Text>
                        }
                    >
                        <Text color={'light'} variant={'p2'} sx={{ textDecoration: 'underline' }}>
                            Tips on writing your evaluation
                        </Text>
                    </Tooltip>
                </HeaderStack>
                <BodyStack spacing={7}>
                    <Stack spacing={1}>
                        <RichTextArea
                            error={errors.details}
                            placeholder={'Add Comments'}
                            helperText={`${stripHTML(submissionData?.details)?.length || 0} characters ${
                                currentPeerEvaluation.is_comment_required
                                    ? `(minimum ${currentPeerEvaluation.min_feedback_length})`
                                    : ''
                            }`}
                            helperTextPosition={'end'}
                            value={submissionData.details}
                            onChange={(html) => handleInputs('details', html)}
                        />
                    </Stack>
                </BodyStack>
            </TableBox>
        );
    };

    const renderSubmittedView = () => {
        return (
            <TableBox key={member?.owner_account_id}>
                <HeaderStack direction={'row'} spacing={1}>
                    <Text variant={'sh2'}>Please Evaluate: </Text>
                    <Text variant={'sh2'} color={'primary'}>
                        {getUserPreferredFullName(member)}
                    </Text>
                </HeaderStack>
                <BodyStack spacing={7}>
                    {!commentOnly && (
                        <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                            <Text variant={'p2'} weight={'medium'}>{`Rating: ${submissionData?.rate || 0}%`}</Text>
                        </Stack>
                    )}
                    <Stack spacing={3} sx={{ minHeight: 10 }}>
                        <Text variant={'p2'} weight={'medium'}>
                            Explain why this rating was given.
                        </Text>
                        {submissionData.details?.length ? (
                            <Text rich variant={'p2'}>
                                {`${submissionData.details || ''} (${submissionData.details?.length || 0} characters)`}
                            </Text>
                        ) : (
                            <Text color={'disabled'} variant={'p2'}>
                                No comment submitted
                            </Text>
                        )}
                    </Stack>
                </BodyStack>
            </TableBox>
        );
    };

    const renderInputView = () => {
        return (
            <TableBox key={member?.owner_account_id}>
                <HeaderStack direction={'row'} spacing={1}>
                    <Text variant={'sh2'}>Please Evaluate: </Text>
                    <Text variant={'sh2'} color={'primary'}>
                        {getUserPreferredFullName(member)}
                    </Text>
                </HeaderStack>
                <BodyStack spacing={7}>
                    {!commentOnly && (
                        <Stack>
                            <CustomBox>
                                <Stack alignItems={'center'} direction={'row'} spacing={2}>
                                    <Text variant={'p2'} weight={'medium'}>
                                        Rating
                                    </Text>
                                    <Tooltip
                                        maxWidth={160}
                                        title={
                                            <Stack divider={<Divider />} spacing={1}>
                                                <Stack>
                                                    <Text color={'inherit'} variant={'p3'}>
                                                        How to: Zero-Sum Peer Evaluation
                                                    </Text>
                                                </Stack>
                                                <Stack spacing={4}>
                                                    <Text color={'inherit'} variant={'p3'}>
                                                        You begin with 100% to allocate between yourself, and your group
                                                        members. For example if you have four group members it might
                                                        look something like this:
                                                    </Text>
                                                    <Stack>
                                                        <Text color={'inherit'} variant={'p3'}>
                                                            Student A: 25%
                                                        </Text>
                                                        <Text color={'inherit'} variant={'p3'}>
                                                            Student B: 30%
                                                        </Text>
                                                        <Text color={'inherit'} variant={'p3'}>
                                                            Student C: 20%
                                                        </Text>
                                                        <Text color={'inherit'} variant={'p3'}>
                                                            Student D: 25%
                                                        </Text>
                                                    </Stack>
                                                    <Stack>
                                                        <Text color={'inherit'} variant={'p3'}>
                                                            for a total of 100% distributed four ways.
                                                        </Text>
                                                    </Stack>
                                                </Stack>
                                            </Stack>
                                        }
                                        type={'info'}
                                    />
                                </Stack>
                                <Stack alignItems={'center'} direction={'row'} spacing={3}>
                                    <FeedbackMinNumberTextInput
                                        data-cy={'zero-sum-student-input'}
                                        error={errors.rate}
                                        placeholder={'enter %'}
                                        value={submissionData?.rate || ''}
                                        onChange={(e) => handleInputs('rate', e.target.value)}
                                    />
                                    <Text variant={'p2'} color={errors.rate ? 'error' : 'light'}>
                                        with {getLeftPercentage()}% remaining to distribute
                                    </Text>
                                </Stack>
                            </CustomBox>
                        </Stack>
                    )}

                    <Stack spacing={1}>
                        <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                            <Text variant={'p2'} weight={'medium'}>
                                Explain why this rating was given.
                            </Text>
                            <Tooltip
                                title={
                                    <Text color={'inherit'} variant={'p2'}>
                                        {currentPeerEvaluation.comment ||
                                            'Tips: Be honest, but professional. Provide detailed explanations of areas that your teammate did well in and areas they can improve. Explain how improving this area can benefit the individual.'}
                                    </Text>
                                }
                            >
                                <Text color={'light'} variant={'p2'} sx={{ textDecoration: 'underline' }}>
                                    Tips on writing your evaluation
                                </Text>
                            </Tooltip>
                        </Stack>
                        <RichTextArea
                            error={errors.details}
                            placeholder={'Add Comments'}
                            helperText={`${stripHTML(submissionData?.details)?.length || 0} characters ${
                                currentPeerEvaluation.is_comment_required
                                    ? `(minimum ${currentPeerEvaluation.min_feedback_length})`
                                    : ''
                            }`}
                            helperTextPosition={'end'}
                            value={submissionData.details}
                            onChange={(html) => handleInputs('details', html)}
                        />
                    </Stack>
                </BodyStack>
            </TableBox>
        );
    };

    const renderView = () => {
        if (commentOnly) {
            return isPreviewing ? renderCommentOnlySubmittedView() : renderCommentOnlyView();
        }
        return isPreviewing ? renderSubmittedView() : renderInputView();
    };

    return renderView();
};

const TableBox = styled(Box)(({ theme }) => {
    return {
        borderRadius: theme.spacing(3),
        border: `1px solid ${theme.palette.grey[300]}`,
    };
});

const HeaderStack = styled(Stack)(({ theme }) => {
    return {
        borderRadius: theme.spacing(3, 3, 0, 0),
        padding: theme.spacing(5, 7),
        backgroundColor: theme.palette.grey[50],
        borderBottom: `1px solid ${theme.palette.grey[300]}`,
    };
});

const BodyStack = styled(Stack)(({ theme }) => {
    return {
        padding: theme.spacing(7),
    };
});

const CustomBox = styled(Box)(({ disabled, theme }) => {
    return {
        display: 'grid',
        gridTemplateColumns: '5.625rem 1fr',
        gridGap: theme.spacing(1, 0),
        alignItems: 'center',
        width: '100%',
        ...(disabled && {
            opacity: 0.5,
        }),
    };
});

const FeedbackMinNumberTextInput = styled(TextInput)(() => {
    return {
        width: '80px',
    };
});

export default ZeroSumGameTable;
